import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import CustomInput from "Common/CustomInput/CustomInput";
import CustomSelect from "Common/CustomSelect/CustomSelect";
import SuperAccordion from "Components/SuperAccordion/SuperAccordion";
import { ALL_INSURANCE_COVERAGES, relationship, TODAY_DATE } from "Common/Constants";
import InsurancePlanSuperSearch from "Components/InsurancePlanSuperSearch/InsurancePlanSuperSearch";
import SubscriberInformation from "Components/InsuranceInformationSections/SubscriberInformation";
import PropTypes from "prop-types";

const shouldShowSubscriberPrompt = (planData) => {
  if (!planData?.relationShip || planData.relationShip === "18") return false;
  const first = (planData?.firstName ?? "").trim();
  const last = (planData?.lastName ?? "").trim();
  const dob = (planData?.dob ?? "").trim();
  const gender = (planData?.gender ?? "").trim();
  const address1 = (planData?.address1 ?? "").trim();
  const zipCode = (planData?.zipCode ?? "").trim();
  const allFilled =
    first !== "" &&
    last !== "" &&
    dob !== "" &&
    gender !== "" &&
    address1 !== "" &&
    zipCode !== "";
  return !allFilled;
};

/**
 * Reusable plan information section.
 * Pass hasInsuranceDataStarted (function returning boolean) when required fields
 * depend on user having started insurance (e.g. New Patient). Omit for edit flows
 * where fields are always required.
 */
const PlanInformation = ({
  planData,
  planIndex,
  coverageType,
  ptModel,
  insuranceSectionsOpen,
  onToggleInsuranceSection,
  handleChange,
  handlePayerSearchChange,
  handleSelectedData,
  hasInsuranceDataStarted,
  validationModel,
  onPasteZip,
  onSameAsPatientAddressChange,
}) => {
  const isRequired = hasInsuranceDataStarted ? hasInsuranceDataStarted() : true;

  // One coverage per plan only: options update when user changes any plan (derived from ptModel).
  const currentCoverage = planData?.coverage || coverageType;
  const plans = ptModel?.patientPlan || [];
  const usedCoveragesByOtherPlans = plans
    .filter((_, i) => i !== planIndex)
    .map((p) => p?.coverage)
    .filter(Boolean);
  const coverageOptions = ALL_INSURANCE_COVERAGES.filter((opt) => {
    if (!opt.value) return false;
    return !usedCoveragesByOtherPlans.includes(opt.value);
  });
  const effectiveCoverageValue = coverageOptions.some(
    (o) => o.value === currentCoverage
  )
    ? currentCoverage
    : "";

  const usedByOthersKey = usedCoveragesByOtherPlans.join(",");
  useEffect(() => {
    if (
      currentCoverage &&
      usedCoveragesByOtherPlans.includes(currentCoverage)
    ) {
      handleChange({
        target: {
          name: "coverage",
          value: "",
          dataset: { nested: `patientPlan.${planIndex}.coverage` },
        },
      });
    }
  }, [currentCoverage, planIndex, usedByOthersKey, handleChange]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="border rounded-lg p-4"
    >
      <div
        className="flex items-center justify-between cursor-pointer py-2"
        onClick={() => onToggleInsuranceSection("plan")}
      >
        <h3 className="text-lg font-medium text-gray-900">
          Plan Information
        </h3>
        {insuranceSectionsOpen.plan ? (
          <FiChevronUp className="text-gray-500" />
        ) : (
          <FiChevronDown className="text-gray-500" />
        )}
      </div>
      <SuperAccordion show={insuranceSectionsOpen.plan}>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-12">
              <InsurancePlanSuperSearch
                label="Payer"
                labelRequired={isRequired}
                searchValue={planData?.insurancePayerName || ""}
                onChange={handlePayerSearchChange}
                handleSelectedData={handleSelectedData}
                toolTip="Insurance company or organization responsible for covering medical expenses"
              />
              {validationModel.insurancePayerError}
            </div>
          </div>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <CustomSelect
                onChange={handleChange}
                name="coverage"
                data-nested={`patientPlan.${planIndex}.coverage`}
                value={effectiveCoverageValue}
                label="Coverage"
              >
                {coverageOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.text}
                  </option>
                ))}
              </CustomSelect>
            </div>
            <div className="col-span-12 md:col-span-6">
              <CustomInput
                onChange={handleChange}
                name="subscriberId"
                data-nested={`patientPlan.${planIndex}.subscriberId`}
                value={planData?.subscriberId || ""}
                label="Subscriber ID"
                required={isRequired}
              />
              {validationModel.insuranceSubscriberIdError}
            </div>
          </div>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <CustomInput
                name="planBeginDate"
                data-nested={`patientPlan.${planIndex}.planBeginDate`}
                onChange={handleChange}
                required={isRequired}
                value={planData?.planBeginDate?.split("T")[0] || ""}
                min="1900-01-01"
                max={TODAY_DATE}
                type="date"
                label="Coverage Date From"
              />
              {validationModel.planBeginDateError}
            </div>
            <div className="col-span-12 md:col-span-6">
              <CustomInput
                name="planEndDate"
                data-nested={`patientPlan.${planIndex}.planEndDate`}
                onChange={handleChange}
                value={planData?.planEndDate?.split("T")[0] || ""}
                type="date"
                label="Coverage Date To"
              />
              {validationModel.coverageStartEndDateError}
            </div>
          </div>
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 md:col-span-6">
              <CustomSelect
                onChange={handleChange}
                name="eligibilityStatus"
                data-nested={`patientPlan.${planIndex}.eligibilityStatus`}
                value={planData?.eligibilityStatus}
                label="Status"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </CustomSelect>
            </div>
            <div className="col-span-12 md:col-span-6">
              <CustomSelect
                onChange={handleChange}
                name="relationShip"
                data-nested={`patientPlan.${planIndex}.relationShip`}
                value={planData?.relationShip}
                label="Relationship to Insured"
                required={isRequired}
              >
                {relationship.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.display}
                  </option>
                ))}
              </CustomSelect>
              {validationModel.insuranceRelationShipError}
              {shouldShowSubscriberPrompt(planData) && (
                <p className="text-sm text-primary mt-1">
                  Please enter the subscriber information.
                </p>
              )}
            </div>
          </div>

          <SubscriberInformation
            ptModel={ptModel}
            validationModel={validationModel}
            handleChange={handleChange}
            onPasteZip={onPasteZip}
            planIndex={planIndex}
            onSameAsPatientAddressChange={onSameAsPatientAddressChange}
            embedded
          />
        </div>
      </SuperAccordion>
    </motion.div>
  );
};

PlanInformation.propTypes = {
  planData: PropTypes.object,
  planIndex: PropTypes.number.isRequired,
  coverageType: PropTypes.string,
  ptModel: PropTypes.object,
  insuranceSectionsOpen: PropTypes.object.isRequired,
  onToggleInsuranceSection: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  handlePayerSearchChange: PropTypes.func.isRequired,
  handleSelectedData: PropTypes.func.isRequired,
  hasInsuranceDataStarted: PropTypes.func,
  validationModel: PropTypes.object,
  onPasteZip: PropTypes.func,
  onSameAsPatientAddressChange: PropTypes.func,
};

export default PlanInformation;
